import { useState, useLayoutEffect, useCallback, } from 'react'
import { useProfile } from 'pages/profile/hooks/useProfile'
import { useContentApi } from 'hooks/use-content-api/useContentApi'
import {
  ContentFeed,
  Content,
  BackendContent,
  JobProps,
  ReportedContent,
  ReportAction,
  ReportedUser,
  ReportActivity,
  QuestionAnswer,
  JobOrderCriteria
} from 'common/types'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useInterval } from 'hooks/use-interval/useInterval'
import { useHistory } from 'react-router-dom'
import { useJobsApi } from 'hooks/use-jobs-api/useJobsApi'
import { BackendPaginatedResponse } from '../../../common/types/index'
import { IMentionable } from 'common/types/mentions.types'

export const useContent = (dontCall?: boolean, showJobs?: boolean, getTagged?: boolean, topic?: string) => {
  const { onWelcome, profile, editPageNumber, setEditPageNumber } = useProfile(dontCall)
  const { backendUser, backendPartner, setForwardingUrl } = useAuth()
  const {
    fetchAllContent,
    createContent,
    deleteContent,
    fetchComments,
    createReaction,
    fetchReactionCount,
    fetchUserReaction,
    createComment,
    deleteComment,
    reportContent,
    updateComment,
    createSharePost,
    fetchCommentCount: fetchCommentCountAll,
    editContent,
    editSharedPost,
    getPost,
    fetchAllReports,
    getReportById,
    getContentById,
    fetchAllReportedUsers,
    reportedUserActions,
    fetchAllSearchedReportedUsers,
    deleteReportedContent,
    fetchReportByContentId,
    fetchReportsByContentId,
    fetchTaggedContent,
    fetchReportActivityLogs
  } = useContentApi()
  const [posts, setPosts] = useState<Content[]>([])
  const [postCount, setPostCount] = useState<number>(0)
  const [networkPosts, setNetworkPosts] = useState<Content[]>([])
  const [networkPostCount, setNetworkPostCount] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [myPage, setMyPage] = useState(1)
  const [networkPage, setNetworkPage] = useState(1)
  const [showClearModal, setShowClearModal] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [commentList, setCommentList] = useState<Content[]>()
  const [myPosts, setMyPosts] = useState<Content[]>([])
  const [myPostCount, setMyPostCount] = useState<number>(0)
  const [showLoader, setShowLoader] = useState(false)
  const [contentTab, setContentTab] = useState(0)
  const [newPostsAvailable, setNewPostsAvailable] = useState(false)
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [showInviteFriends, setShowInviteFriends] = useState(false)
  const history = useHistory()
  const { getMemberJobs } = useJobsApi()
  const [teaSpotJobs, setTeaSpotJobs] = useState<JobProps[]>()
 

  const questions: QuestionAnswer[] = backendUser?.onboardingQuestions
  const userAreaOfExpertise = questions.find(ans => ans.question.trim() === "Area of Expertise")
  const userIndustry = questions.find(ans => ans.question.trim() === "Industry")

  const jobsOrderCriteria: JobOrderCriteria = {
    areaOfExpertise: userAreaOfExpertise?.answer,
    industry: userIndustry?.answer
  }
  
  const getData = useCallback(async (shouldReset = false) => {
    switch (contentTab) {
      case 0: {
        const fetchPostsPromise = getTagged ? fetchTaggedContent(page, 5, topic) : fetchAllContent(backendUser?.id, page, 5)

        const allPosts: ContentFeed = await fetchPostsPromise
        const uniquePost = allPosts?.data?.filter(x => {
          const existingPost = posts?.find(y => y?.id === x?.id)
          if (!existingPost) return x
        })

        let _posts = posts

        if (getTagged) {
          if (page == 1) {
            _posts = shouldReset ? [] : posts
          }
        }

        setPosts(_posts?.concat(uniquePost))
        setPostCount(allPosts?.totalCount)
        if (showJobs) {
          const jobsResponse = await getMemberJobs(1, 10, jobsOrderCriteria)
          if (jobsResponse) {
            setTeaSpotJobs(jobsResponse.data)
          }
        }
        break
      }
    }
  }, [contentTab, page, topic])

  const getAllPosts = async () => {
    const allPosts: ContentFeed = await fetchAllContent(backendUser?.id, page, 5)
    const uniquePost = allPosts?.data?.filter(x => {
      const existingPost = posts?.find(y => y?.id === x?.id)
      if (!existingPost) return x
    })
    const notBackendUserPosts = uniquePost?.every(post => post?.userId !== backendUser?.id)
    if (notBackendUserPosts && allPosts?.totalCount > postCount) {
      setNewPostsAvailable(true)
    }
  }

  const getRefreshedFeed = async () => {
    const allPosts: ContentFeed = await fetchAllContent(backendUser?.id, 1, 5)
    const uniquePost = allPosts?.data?.filter(x => {
      const existingPost = posts?.find(y => y?.id === x?.id)
      if (!existingPost) return x
    })
    posts?.unshift(...uniquePost)
    setPosts(posts)
    setPostCount(allPosts?.totalCount)
    const jobsResponse = await getMemberJobs(1, 10, jobsOrderCriteria)
    if (jobsResponse) {
      setTeaSpotJobs(jobsResponse.data)
    }
  }

  useLayoutEffect(() => {
    if (location.pathname.includes('invite')) setShowInviteFriends(true)
    if (location.pathname.includes('post/create')) setShowCreatePost(true)
  }, [location.pathname])

  useLayoutEffect(() => {
    if (backendUser) {
      if (!backendUser?.isOnBoarded) {
        history.push('/settings')
        return
      }
      getData(true)
    }
  }, [contentTab, page, topic])

  useInterval(() => {
    getAllPosts()
  }, 300000)

  const setNewPage = () => {
    setPage(page + 1)
  }
  const setNewMyPage = () => {
    setMyPage(myPage + 1)
  }
  const setNewNetworkPage = () => {
    setNetworkPage(networkPage + 1)
  }

  const handleComment = async (
    userId: string,
    postId: string,
    comment: string,
    partnerId?: string,
    mentions?: IMentionable[],
    tags?: string[]
  ) => {
    const userComment: BackendContent = await createComment(userId, postId, comment, partnerId, mentions, tags)
    return userComment
  }

  const fetchCommentCount = async (contentId: string) => {
    return await fetchCommentCountAll(contentId)
  }

  const getReport = async (reportId: string) => {
    const report: Partial<ReportedContent> = await getReportById(reportId)
    return report
  }

  const getAllReportedUsers = async (userId: string, page: number, pageSize: number) => {
    const reports: BackendPaginatedResponse<ReportedUser> = await fetchAllReportedUsers(userId, page, pageSize)
    return reports
  }

  const getAllSearchedUsers = async (
    userId: string,
    page: number,
    pageSize: number,
    searchCriteria?: string,
    status?: string
  ) => {
    const reports: BackendPaginatedResponse<ReportedUser> = await fetchAllSearchedReportedUsers(
      userId,
      page,
      pageSize,
      searchCriteria,
      status,
    )
    return reports
  }

  const reporter = async (contentId: string) => {
    const reportedContent = await fetchReportByContentId(contentId)
    return reportedContent
  }

  return {
    backendUser,
    backendPartner,
    setForwardingUrl,
    onWelcome,
    profile,
    posts,
    loading,
    createContent,
    createComment,
    showClearModal,
    setShowClearModal,
    showEdit,
    setShowEdit,
    setPosts,
    commentList,
    setCommentList,
    deleteContent,
    myPosts,
    networkPosts,
    setMyPosts,
    showLoader,
    setShowLoader,
    contentTab,
    setContentTab,
    fetchComments,
    setPage,
    createReaction,
    fetchReactionCount,
    fetchUserReaction,
    deleteComment,
    reportContent,
    createSharePost,
    updateComment,
    fetchCommentCount,
    changeTab: { setNewPage, setNewMyPage, setNewNetworkPage },
    allCounts: { postCount, networkPostCount, myPostCount },
    handleComment,
    editContent,
    newPostsAvailable,
    setNewPostsAvailable,
    editSharedPost,
    getPost,
    editPageNumber,
    setEditPageNumber,
    getRefreshedFeed,
    showCreatePost,
    setShowCreatePost,
    showInviteFriends,
    setShowInviteFriends,
    teaSpotJobs,
    getAllReportedUsers,
    reportedUserActions,
    getReport,
    getContentById,
    fetchAllReports,
    getAllSearchedUsers,
    deleteReportedContent,
    fetchReportByContentId,
    reporter,
    fetchReportsByContentId,
    fetchReportActivityLogs
  } as const
}
